import { useAppSelector } from '@/store/hooks';
import { getRSVPLinkSync } from '@/store/slices/integrations/selectors';
import { SessionTypesEnum } from '@/types/session.types';
import {
  getWorkingProjectSessions,
  selectWorkingProjectRegistrationEnabled,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectSocialSettings,
  selectWorkingProjectSettings,
} from '@/store/slices/project/selectors';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import { useMemo } from 'react';
import { RSVPLinkSyncStatus } from '@/types/rsvp.types';
import { PageModuleType, QuestionPrompt } from '@/types/module.types';
import { RegistrationStepType, RegistrationTypes } from '@/types/registration.types';
import { isCustomSsoEnabledForSettings } from '@/utils/authentication/sso-utils';

export default function useComputedRegistrationSettings() {
  const rsvpLinkSync = useAppSelector(getRSVPLinkSync);
  const workingProjectSessions = useAppSelector(getWorkingProjectSessions);
  const workingProjectSocialSettings = useAppSelector(selectWorkingProjectSocialSettings);
  const workingProjectSettings = useAppSelector(selectWorkingProjectSettings);

  const registrationOn = useAppSelector(selectWorkingProjectRegistrationEnabled) || false;
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps) || [];
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const singleSignOnEnabled = registrationSettings?.singleSignOn?.isOn || false;
  // True only when the user is in custom-SSO mode (SAML/OIDC/oAuth or empty SSO panel).
  // Returns false for "Native + social" states where singleSignOn.isOn is true but the only
  // active sign-on is a social provider (Google) — those should keep showing the Native panel.
  const customSsoEnabled = isCustomSsoEnabledForSettings(registrationSettings);
  const directoryOn = workingProjectSocialSettings?.directory?.isOn;
  const messagingIsOn = workingProjectSocialSettings?.messaging;
  const myListIsOn = workingProjectSettings?.personalizedAttendeeList;
  const courseOn = workingProjectSettings?.course_enabled;
  const leaderboardEnabled = !!workingProjectSettings?.leaderboard_settings?.enabled; // enabled in settings and flag on
  const ticketingOn = registrationSteps?.find((step) => step.type === RegistrationStepType.ticketing)?.isOn;
  const muxDrmEnabled = useCanUseChannelFeature(ChannelFeaturesEnum.mux_drm);
  const rsvpLinkSyncApproved = rsvpLinkSync?.status === RSVPLinkSyncStatus.approved;
  // Password gating is enforced from password_gating_enabled alone — no code path conditions it on
  // registration_settings.type, so type never turns password enforcement on or off. The `&&` below only
  // decides which controls the editor disables or hides in the UI: turning the password gate on sets
  // type=gated, then switching to SSO/3rd Party/Disabled forces type=open while leaving
  // password_gating_enabled=true behind so Native can restore the gate — don't treat that leftover as active.
  const accessRestrictionsOn = registrationSettings?.type === RegistrationTypes.gated;
  const passwordGateEnabled = accessRestrictionsOn && !!registrationSettings?.password_gating_enabled;

  const watermarkOn = useMemo(() => {
    return workingProjectSessions?.some((session) => session?.streaming_options?.watermark);
  }, [workingProjectSessions]);

  const containsBreakoutSession = useMemo(() => {
    return workingProjectSessions?.some((session) => session.session_type === SessionTypesEnum.breakoutRooms);
  }, [workingProjectSessions]);

  const containsFiresideSession = useMemo(() => {
    return workingProjectSessions?.some((session) => session.session_type === SessionTypesEnum.fireside);
  }, [workingProjectSessions]);

  const containsPrivateResponses = useMemo(() => {
    return (
      workingProjectSessions?.some((session) =>
        session.modules?.some(
          (module) =>
            module.type === PageModuleType.questions &&
            (module.modules as QuestionPrompt[])?.some((item) => item?.settings?.private_responses)
        )
      ) ?? false
    );
  }, [workingProjectSessions]);

  const containsBreakoutOrFiresideSession = containsBreakoutSession || containsFiresideSession;

  const registrationRequired =
    containsBreakoutOrFiresideSession ||
    directoryOn ||
    messagingIsOn ||
    myListIsOn ||
    leaderboardEnabled ||
    watermarkOn ||
    containsPrivateResponses;

  return {
    registrationOn,
    directoryOn,
    messagingIsOn,
    myListIsOn,
    leaderboardEnabled,
    watermarkOn,
    containsPrivateResponses,
    containsBreakoutOrFiresideSession,
    registrationRequired,
    rsvpLinkSyncApproved,
    muxDrmEnabled,
    courseOn,
    singleSignOnEnabled,
    customSsoEnabled,
    passwordGateEnabled,
    accessRestrictionsOn,
    registrationSteps,
    registrationSettings,
    ticketingOn,
  };
}
